import type { Recapt as RecaptEngine } from './engine';
import type {
  EventTypes,
  FeedbackPayload,
  StartOptions,
  TrackEvent,
  UserCommentPositioning,
} from './types';

let engine: typeof RecaptEngine | null = null;

export function bindEngine(e: typeof RecaptEngine) {
  engine = e;
}

/**
 * Public facade for the Recapt SDK.
 * Exposes only safe methods while keeping internal state inaccessible.
 */
export class RecaptFacade {
  get running(): boolean {
    return engine?.running ?? false;
  }

  get sessionId(): string {
    return engine?.sessionId ?? '';
  }

  start(opts: StartOptions): void {
    engine?.start(opts);
  }

  stop(): void {
    engine?.stop();
  }

  pause(): void {
    engine?.pause();
  }

  resume(): void {
    engine?.resume();
  }

  persist(): void {
    engine?.persist();
  }

  async terminate(): Promise<void> {
    await engine?.terminate();
  }

  destroy(): void {
    engine?.destroy();
  }

  reset(): void {
    engine?.reset();
  }

  suspend(): void {
    engine?.suspend();
  }

  continue(): void {
    engine?.continue();
  }

  addAction(action: string): void {
    engine?.addAction(action);
  }

  tag(tag: string): void {
    engine?.tag(tag);
  }

  track(event: TrackEvent): void {
    engine?.track(event);
  }

  feedback(payload: FeedbackPayload): void {
    engine?.feedback(payload);
  }

  enableUserComments(pos?: UserCommentPositioning): void {
    engine?.enableUserComments(pos);
  }

  useSystemPainpoints(): void {
    engine?.useSystemPainpoints();
  }

  saveSession(): void {
    engine?.saveSession();
  }

  once(event: EventTypes, callback: (data: any) => void): void {
    engine?.once(event, callback);
  }
}
